import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Image URL field with a file-upload shortcut.
 * `preset` (optional): "founder" | "testimonial" | "og" | "program" — enables auto-crop server-side.
 */
export default function ImageUploadField({ label, value, onChange, testid, preset, altValue, onAltChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    setBusy(true);
    const form = new FormData();
    form.append("file", f);
    if (preset) form.append("preset", preset);
    if (altValue) form.append("alt_text", altValue);
    try {
      const { data } = await api.post("/media/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success(preset ? `Uploaded & auto-cropped (${preset})` : "Uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sm:col-span-2 space-y-3">
      <label className="block">
        <span className="text-[11px] uppercase tracking-widest text-slate-500">{label}{preset ? ` · auto-crop ${preset}` : ""}</span>
        <div className="mt-1.5 flex items-stretch gap-2">
          <input
            data-testid={testid}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste an image URL or upload a file"
            className="flex-1 h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm"
          />
          <button type="button" onClick={pick} disabled={busy}
                  className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-[#0B132B] text-white text-sm disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {busy ? "Uploading…" : "Upload"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")}
                    className="h-11 w-11 grid place-items-center rounded-xl ring-1 ring-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Clear">
              <X size={14} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleFile} className="hidden" />
        </div>
      </label>
      {onAltChange && (
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest text-slate-500">Alt text (image description for accessibility & SEO)</span>
          <input value={altValue ?? ""} onChange={(e) => onAltChange(e.target.value)}
                 placeholder="e.g. Shubhi Jain, Founder of Be Hire Ready"
                 className="mt-1.5 w-full h-11 px-4 rounded-xl bg-white ring-1 ring-slate-200 focus:ring-[#0B132B] focus:outline-none text-sm" />
        </label>
      )}
      {value && (
        <div className="flex items-center gap-3">
          <img src={value} alt="preview" className="h-16 w-16 object-cover rounded-lg ring-1 ring-slate-200 bg-slate-50" />
          <div className="text-xs text-slate-500 truncate">{value}</div>
        </div>
      )}
    </div>
  );
}
